import { useQuery } from '@apollo/client';
import { uniqBy } from 'ramda';
import { useCallback } from 'react';
import { useCountry } from '../../components/CountrySelector';
import { UserRewardStatus } from '../../constants';
import { useUser } from '../useUser';
import {
  GET_REWARDS_SUMMARY_QUERY,
  GetRewardsSummaryQuery,
  GetRewardsSummaryQueryVariables
} from './rewardsSummary.rest';

const PAGE_SIZE = 20;

export const useRewardsSummary = (currency: string) => {
  const {
    country: { alpha2Code }
  } = useCountry();
  const {
    user: { userLoyaltyStatus }
  } = useUser();

  const { data, loading, error, fetchMore: fetchMoreFn } = useQuery<
    GetRewardsSummaryQuery,
    GetRewardsSummaryQueryVariables
  >(GET_REWARDS_SUMMARY_QUERY, {
    variables: {
      countryCode: alpha2Code,
      currency,
      pageNumber: 1,
      pageSize: PAGE_SIZE
    },
    skip: userLoyaltyStatus !== UserRewardStatus.Enrolled,
    notifyOnNetworkStatusChange: true
  });

  const attributes = data?.getRewardsSummary.data[0]?.attributes;

  const currentPageNumber = attributes?.getRewardsSummary.currentPageNumber || 1;
  const totalRecords = attributes?.getRewardsSummary.totalRecords || 0;

  const updateQuery = useCallback(
    (previousResults: GetRewardsSummaryQuery, { fetchMoreResult }) => ({
      ...fetchMoreResult,
      getRewardsSummary: {
        ...fetchMoreResult.getRewardsSummary,
        data: [
          {
            ...fetchMoreResult.getRewardsSummary.data[0],
            attributes: {
              ...fetchMoreResult.getRewardsSummary.data[0].attributes,
              getRewardsSummary: {
                ...fetchMoreResult.getRewardsSummary.data[0].attributes.getRewardsSummary,
                summary: uniqBy(_ => _.id, [
                  ...previousResults.getRewardsSummary.data[0].attributes.getRewardsSummary.summary,
                  ...fetchMoreResult.getRewardsSummary.data[0].attributes.getRewardsSummary.summary
                ])
              }
            }
          }
        ]
      }
    }),
    []
  );

  const fetchMore = useCallback(() => {
    fetchMoreFn({
      updateQuery,
      variables: {
        pageNumber: currentPageNumber + 1
      }
    });
  }, [currentPageNumber, fetchMoreFn, updateQuery]);

  return {
    rewardsData: attributes?.getRewardsData,
    rewardsSummary: attributes?.getRewardsSummary.summary,
    expiresOn: attributes?.expiresOn,
    loading,
    error,
    fetchMore: PAGE_SIZE * currentPageNumber <= totalRecords ? fetchMore : undefined,
    totalRecords
  };
};
